<header class="topbar">
  <div class="topbar-left">
    <button class="hamburger icon-btn" (click)="toggleNav()" aria-label="Toggle navigation">
      <mat-icon>menu</mat-icon>
    </button>
    <div class="greeting">
      <span class="hi">Welcome back</span>
      <span class="name">{{ firstName || 'there' }}</span>
    </div>
  </div>

  <div class="topbar-right">
    <!-- Individual / Business toggle. Not while the application waits for approval: there is no
         account to act as yet. -->
    @if (!merchants.awaitingApproval()) {
    <div class="account-toggle">
      <button [class.active]="accountType === 'Individual'" (click)="setAccountType('Individual')">
        Individual
      </button>
      <button [class.active]="accountType === 'Business'" (click)="setAccountType('Business')">
        Business
      </button>
    </div>

    <!-- Business account selector -->
    @if (accountType === 'Business') {
      @if (hasBusinesses && selectedBusiness) {
        <div class="business-selector" [matMenuTriggerFor]="bizMenu">
          <mat-icon class="biz-icon">corporate_fare</mat-icon>
          <span>{{ selectedBusiness.name }}</span>
          @if (!selectedBusiness.canTransact) {
            <mat-icon class="biz-warn" [matTooltip]="selectedBusiness.kycStatusLabel">pending</mat-icon>
          }
          <mat-icon class="caret">expand_more</mat-icon>
        </div>
        <mat-menu #bizMenu="matMenu" class="biz-menu">
          @for (b of businessAccounts; track b.id) {
            <button mat-menu-item (click)="selectBusiness(b)" [class.selected]="b.id === selectedBusiness.id">
              <mat-icon>corporate_fare</mat-icon>
              <span class="biz-name">{{ b.name }}</span>
              @if (!b.canTransact) {
                <span class="biz-status">{{ b.kycStatusLabel }}</span>
              }
            </button>
          }
          <mat-divider></mat-divider>
          <button mat-menu-item routerLink="/business/overview">
            <mat-icon>settings</mat-icon>
            <span>Manage business accounts</span>
          </button>
          <button mat-menu-item routerLink="/business/entities/new">
            <mat-icon>add_business</mat-icon>
            <span>Add business account</span>
          </button>
        </mat-menu>
      } @else if (loadingBusinesses) {
        <div class="business-selector is-loading">
          <mat-icon class="biz-icon">corporate_fare</mat-icon>
          <span>Loading…</span>
        </div>
      } @else {
        <!-- No business accounts yet: the only sensible action is to create the first one. -->
        <button class="business-selector is-empty" (click)="addBusiness()">
          <mat-icon class="biz-icon">add_business</mat-icon>
          <span>Add your first business account</span>
        </button>
      }
    }
    }

    <!-- Language -->
    <div class="lang-badge">
      <span class="flag">🇬🇧</span>
      <span>UK</span>
    </div>

    <!-- BTC price ticker. Hidden until the live rate arrives — it used to fall back to a
         hardcoded price, which read as real. -->
    @if (btcPrice > 0) {
      <div class="btc-ticker">
        <span class="btc-icon">₿</span>
        <span class="btc-price">\${{ btcPrice | number:'1.2-2' }}</span>
      </div>
    }

    <!-- Theme toggle -->
    <button class="icon-btn" (click)="theme.toggle()"
            [attr.aria-label]="theme.theme() === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
            matTooltip="{{ theme.theme() === 'dark' ? 'Light mode' : 'Dark mode' }}">
      <mat-icon>{{ theme.theme() === 'dark' ? 'light_mode' : 'dark_mode' }}</mat-icon>
    </button>

    <!-- Notifications. The badge reflects the real unread count, polled by NotificationService. -->
    <button class="icon-btn"
            [matMenuTriggerFor]="notificationMenu"
            (menuOpened)="notifications.load()"
            [matBadge]="unreadBadge()"
            [matBadgeHidden]="notifications.unreadCount() === 0"
            matBadgeColor="warn"
            matBadgeSize="small"
            [attr.aria-label]="notifications.unreadCount() > 0
              ? notifications.unreadCount() + ' unread notifications'
              : 'Notifications'"
            matTooltip="Notifications">
      <mat-icon>{{ notifications.unreadCount() > 0 ? 'notifications' : 'notifications_none' }}</mat-icon>
    </button>

    <mat-menu #notificationMenu="matMenu" class="notification-menu" xPosition="before">
      <div class="notification-header" mat-menu-item disabled>
        <span class="notification-title">Notifications</span>
        @if (notifications.unreadCount() > 0) {
          <!-- stopPropagation: marking all read should not also close the menu. -->
          <button class="mark-all" (click)="$event.stopPropagation(); notifications.markAllRead()">
            Mark all read
          </button>
        }
      </div>
      <mat-divider></mat-divider>

      @if (notifications.loading()) {
        <div class="notification-empty" mat-menu-item disabled>
          <span>Loading…</span>
        </div>
      } @else if (notifications.error()) {
        <div class="notification-empty" mat-menu-item disabled>
          <mat-icon>error_outline</mat-icon>
          <span>{{ notifications.error() }}</span>
        </div>
      } @else if (!notifications.items().length) {
        <div class="notification-empty" mat-menu-item disabled>
          <mat-icon>notifications_off</mat-icon>
          <span>Nothing yet. Deposits, withdrawals and scheduled payments will show up here.</span>
        </div>
      } @else {
        @for (item of notifications.items(); track item.id) {
          <button mat-menu-item class="notification-row"
                  [class.is-unread]="item.unread"
                  (click)="openNotification(item)">
            <span class="n-dot severity-{{ item.severity }}"></span>
            <span class="n-content">
              <span class="n-title">{{ item.title }}</span>
              <span class="n-body">{{ item.body }}</span>
              <span class="n-time">{{ item.createdAt | date:'dd MMM, HH:mm' }}</span>
            </span>
          </button>
        }
      }

      <mat-divider></mat-divider>
      <button mat-menu-item routerLink="/settings/notifications">
        <mat-icon>tune</mat-icon>
        <span>Notification settings</span>
      </button>
    </mat-menu>

    <!-- User menu -->
    <button class="user-trigger" [matMenuTriggerFor]="userMenu" aria-label="User menu">
      <span class="user-avatar">{{ initials || 'U' }}</span>
      <mat-icon class="caret">expand_more</mat-icon>
    </button>
    <mat-menu #userMenu="matMenu" class="user-menu" xPosition="before">
      <div class="user-menu-header" mat-menu-item disabled>
        <div class="user-menu-name">{{ displayName || 'User' }}</div>
        <div class="user-menu-email">{{ email }}</div>
      </div>
      <mat-divider></mat-divider>
      <button mat-menu-item routerLink="/settings/profile">
        <mat-icon>person_outline</mat-icon>
        <span>Profile</span>
      </button>
      <mat-divider></mat-divider>
      <button mat-menu-item (click)="logout()">
        <mat-icon>logout</mat-icon>
        <span>Sign out</span>
      </button>
    </mat-menu>
  </div>
</header>
